import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance, { endpoints } from 'src/utils/axios';

// ─── Async Thunks ─────────────────────────────────────────────────────────────

/** Fetch all conversations for the current user */
export const fetchConversations = createAsyncThunk(
  'chat/fetchConversations',
  async ({ archived = false, userId } = {}, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(endpoints.chat.conversations, { params: { archived } });
      return { ...res.data, userId };
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to load conversations');
    }
  }
);

/** Fetch messages for a conversation (cursor pagination) */
export const fetchMessages = createAsyncThunk(
  'chat/fetchMessages',
  async ({ conversationId, before, limit = 30 }, { rejectWithValue }) => {
    try {
      const params = { limit };
      if (before) params.before = before;
      const res = await axiosInstance.get(endpoints.chat.messages(conversationId), { params });
      return { conversationId, messages: res.data.data, hasMore: res.data.hasMore };
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to load messages');
    }
  }
);

/** Create a new conversation */
export const createConversation = createAsyncThunk(
  'chat/createConversation',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(endpoints.chat.conversations, payload);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to create conversation');
    }
  }
);

/** Send a text/location/poll message */
export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  async ({ conversationId, ...payload }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(endpoints.chat.sendMessage(conversationId), payload);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to send message');
    }
  }
);

/** Upload and send a media message */
export const sendMediaMessage = createAsyncThunk(
  'chat/sendMediaMessage',
  async ({ conversationId, file, caption, replyTo }, { rejectWithValue, dispatch }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (caption) formData.append('caption', caption);
      if (replyTo) formData.append('replyTo', replyTo);
      const res = await axiosInstance.post(endpoints.chat.media(conversationId), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          dispatch(updateUploadProgress({ conversationId, fileName: file.name, progress: percentCompleted }));
        }
      });
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to upload media');
    }
  }
);

/** Edit a message */
export const editMessage = createAsyncThunk(
  'chat/editMessage',
  async ({ messageId, body }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(endpoints.chat.editMessage(messageId), { body });
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to edit message');
    }
  }
);

/** Soft-delete a message */
export const deleteMessage = createAsyncThunk(
  'chat/deleteMessage',
  async ({ messageId, conversationId }, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(endpoints.chat.deleteMessage(messageId));
      return { messageId, conversationId };
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to delete message');
    }
  }
);

/** React to a message */
export const reactToMessage = createAsyncThunk(
  'chat/reactToMessage',
  async ({ messageId, emoji }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(endpoints.chat.react(messageId), { emoji });
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to react');
    }
  }
);

/** Mark messages as read */
export const markAsRead = createAsyncThunk(
  'chat/markAsRead',
  async ({ messageId, conversationId }, { rejectWithValue }) => {
    try {
      await axiosInstance.post(endpoints.chat.markRead(messageId));
      return { conversationId };
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to mark as read');
    }
  }
);

/** Mute a conversation */
export const muteConversation = createAsyncThunk(
  'chat/muteConversation',
  async ({ conversationId, mute, duration }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(endpoints.chat.mute(conversationId), { mute, duration });
      return { conversationId, mutedUntil: res.data.data.mutedUntil };
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to mute');
    }
  }
);

/** Archive or unarchive a conversation */
export const archiveConversation = createAsyncThunk(
  'chat/archiveConversation',
  async ({ conversationId, archive }, { rejectWithValue }) => {
    try {
      await axiosInstance.post(endpoints.chat.archive(conversationId), { archive });
      return { conversationId, archive };
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to archive');
    }
  }
);

/** Update privacy settings */
export const updatePrivacySettings = createAsyncThunk(
  'chat/updatePrivacySettings',
  async (settings, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('/api/admin/chat/settings', settings);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to update settings');
    }
  }
);

/** Search org users to start a chat */
export const fetchChatUsers = createAsyncThunk(
  'chat/fetchChatUsers',
  async (search = '', { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(endpoints.chat.users, { params: { search, limit: 30 } });
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to load users');
    }
  }
);

/** Add participant to group */
export const addParticipant = createAsyncThunk(
  'chat/addParticipant',
  async ({ conversationId, userIds }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(endpoints.chat.participants(conversationId), { userIds });
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to add participant');
    }
  }
);

/** Remove participant from group */
export const removeParticipant = createAsyncThunk(
  'chat/removeParticipant',
  async ({ conversationId, userId }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.delete(endpoints.chat.removeParticipant(conversationId, userId));
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to remove participant');
    }
  }
);

/** Update participant role */
export const updateParticipantRole = createAsyncThunk(
  'chat/updateParticipantRole',
  async ({ conversationId, userId, role }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(endpoints.chat.updateParticipantRole(conversationId, userId), { role });
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to update participant role');
    }
  }
);

/** Pin a message */
export const pinMessage = createAsyncThunk(
  'chat/pinMessage',
  async ({ conversationId, messageId }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(endpoints.chat.pinMessage(conversationId, messageId));
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to pin message');
    }
  }
);

/** Unpin a message */
export const unpinMessage = createAsyncThunk(
  'chat/unpinMessage',
  async ({ conversationId, messageId }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(endpoints.chat.unpinMessage(conversationId, messageId));
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to unpin message');
    }
  }
);

/** Vote on a poll */
export const votePoll = createAsyncThunk(
  'chat/votePoll',
  async ({ messageId, optionId }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(endpoints.chat.pollVote(messageId), { optionId });
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to vote');
    }
  }
);

/** Fetch LiveKit token for group calling */
export const fetchLiveKitToken = createAsyncThunk(
  'chat/fetchLiveKitToken',
  async ({ conversationId }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(endpoints.chat.liveKitToken(conversationId));
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to fetch LiveKit token');
    }
  }
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const upsertMessage = (state, message) => {
  const cid = message.conversationId;
  if (!state.messages[cid]) {
    state.messages[cid] = { list: [], hasMore: true, cursor: null };
  }
  const idx = state.messages[cid].list.findIndex(m => m._id === message._id);
  if (idx >= 0) {
    state.messages[cid].list[idx] = message;
  } else {
    state.messages[cid].list.push(message);
  }
};

const upsertConversation = (state, conv) => {
  // Mark current user if known (using both adminRef and _id for robust matching)
  if (conv.participants && (state.currentUserId || state.currentOrgUserId)) {
    conv.participants.forEach(p => {
      const pUserId = p.userId?._id || p.userId;
      const pAdminRef = p.userId?.adminRef;
      
      const isMeByAdmin = state.currentUserId && pAdminRef?.toString() === state.currentUserId.toString();
      const isMeByOrg = state.currentOrgUserId && pUserId?.toString() === state.currentOrgUserId.toString();
      
      if (isMeByAdmin || isMeByOrg) {
        p.isCurrentUser = true;
      }
    });
  }

  const idx = state.conversations.allIds.indexOf(conv._id);
  if (idx >= 0) {
    state.conversations.byId[conv._id] = { ...state.conversations.byId[conv._id], ...conv };
  } else {
    state.conversations.allIds.unshift(conv._id);
    state.conversations.byId[conv._id] = conv;
  }
};

// ─── Slice ────────────────────────────────────────────────────────────────────

const chatSlice = createSlice({
  name: 'chat',
  initialState: {
    // Conversation list (normalized)
    conversations: { byId: {}, allIds: [] },
    activeConversationId: null,
    currentUserId: null,
    currentOrgUserId: null,
    // Messages per conversation
    messages: {},
    // Typing state: { [conversationId]: [userId, ...] }
    typingUsers: {},
    // Total unread across all conversations
    totalUnread: 0,
    activeUnread: 0,
    directUnread: 0,
    groupUnread: 0,
    archivedUnread: 0,
    archivedCount: 0,
    // LiveKit Calling State
    call: {
      active: false,
      receiving: false,
      caller: null,
      callee: null,
      type: 'audio',
      status: 'idle',
      conversationId: null,
      isGroup: false,
      liveKitToken: null,
      liveKitUrl: null,
      minimized: false,
    },
    // Tracks ongoing group calls per conversation so users can join late.
    // Shape: { [conversationId]: { callerId, type, startedAt } }
    activeGroupCalls: {},
    // Active participants per call: { [conversationId]: [{ userId, name, avatarUrl }, ...] }
    callParticipants: {},
    // Search results...
    chatUsers: [],
    settings: {
    showEmail: true,
    showPhone: true,
    showPhoto: true,
  },
  loading: {
      conversations: false,
      messages: false,
      sending: false,
      chatUsers: false,
    },
    error: null,
  },
  reducers: {
    // Update local uploading message progress
    updateUploadProgress(state, action) {
      const { conversationId, fileName, progress } = action.payload;
      if (state.messages[conversationId]) {
        const msg = state.messages[conversationId].list.find(
          (m) => m.status === 'uploading' && m.body === fileName
        );
        if (msg) {
          msg.progress = progress;
        }
      }
    },

    // Set active conversation
    setActiveConversation(state, action) {
      state.activeConversationId = action.payload;
    },

    // Reset unread for a conversation (called after opening it)
    resetUnread(state, action) {
      const conv = state.conversations.byId[action.payload];
      if (conv) {
        const participant = conv.participants?.find((part) => part.isCurrentUser);
        if (participant && participant.unreadCount > 0) {
          const count = participant.unreadCount;
          if (participant.isArchived) {
            state.archivedUnread = Math.max(0, state.archivedUnread - count);
          } else {
            state.activeUnread = Math.max(0, state.activeUnread - count);
            if (conv.type === 'group') state.groupUnread = Math.max(0, state.groupUnread - count);
            else state.directUnread = Math.max(0, state.directUnread - count);
          }
          state.totalUnread = Math.max(0, state.totalUnread - count);
          participant.unreadCount = 0;
        }
      }
    },

    // ─── Socket event handlers (dispatched from useChatSocket) ────────────────

    /** New message received via socket */
    onNewMessage(state, action) {
      const message = action.payload;
      const cid = String(message.conversationId);
      const myId = state.currentUserId?.toString();
      const myOrgId = state.currentOrgUserId?.toString();
      const senderId = (message.sender?._id || message.sender)?.toString();
      
      const isMeByAdmin = myId && senderId === myId;
      const isMeByOrg = myOrgId && senderId === myOrgId;
      const isMe = isMeByAdmin || isMeByOrg;

      upsertMessage(state, message);

      if (state.conversations.byId[cid]) {
        const conv = state.conversations.byId[cid];
        const myParticipant = conv.participants?.find((p) => p.isCurrentUser);
        const isArchived = myParticipant?.isArchived;

        // Update lastMessage
        conv.lastMessage = {
          body: message.type === 'text' ? message.body : `[${message.type}]`,
          senderId: message.sender?._id || message.sender,
          type: message.type,
          createdAt: message.createdAt
        };

        // Increment unread if not current open conversation
        const activeId = state.activeConversationId ? String(state.activeConversationId) : null;
        if (!isMe && cid !== activeId) {
          // Increment specific participant count (match by adminRef OR _id)
          const participant = conv.participants?.find((p) => {
            if (p.isCurrentUser) return true;
            const pUserId = p.userId?._id || p.userId;
            const pAdminRef = p.userId?.adminRef;
            return (myId && pAdminRef?.toString() === myId) || (myOrgId && pUserId?.toString() === myOrgId);
          });
          if (participant) {
            participant.unreadCount = (participant.unreadCount || 0) + 1;
            // Also ensure isCurrentUser is set for future use
            participant.isCurrentUser = true;
            
            if (isArchived) {
              state.archivedUnread += 1;
            } else {
              state.activeUnread += 1;
              if (conv.type === 'group') state.groupUnread += 1;
              else state.directUnread += 1;
            }
          }
          state.totalUnread += 1;
        }

        // Bubble conversation to top
        const idx = state.conversations.allIds.indexOf(cid);
        // If archived, ONLY bubble if already in the current list (meaning we are in the Archived tab)
        // If not archived, always bubble to top
        if (!isArchived || idx !== -1) {
          if (idx !== 0) {
            if (idx > 0) {
              state.conversations.allIds.splice(idx, 1);
            }
            state.conversations.allIds.unshift(cid);
          }
        }
      } else if (!isMe) {
          // Increment global total even if conversation not yet loaded
          state.totalUnread += 1;
      }
    },

    /** Message updated (edit/delete/react) received via socket */
    onMessageUpdated(state, action) {
      const message = action.payload;
      const cid = message.conversationId;
      if (state.messages[cid]) {
        const idx = state.messages[cid].list.findIndex(m => m._id === message._id);
        if (idx >= 0) {
          state.messages[cid].list[idx] = { ...state.messages[cid].list[idx], ...message };
        }
      }
    },

    /** Conversation updated (renamed, participant added etc.) */
    onConversationUpdated(state, action) {
      const conv = action.payload;
      if (conv?._id) {
        const hasCurrentUser = conv.participants?.some(p => {
          const pUserId = p.userId?._id || p.userId;
          const pAdminRef = p.userId?.adminRef;
          const isMeByAdmin = state.currentUserId && pAdminRef?.toString() === state.currentUserId.toString();
          const isMeByOrg = state.currentOrgUserId && pUserId?.toString() === state.currentOrgUserId.toString();
          return isMeByAdmin || isMeByOrg;
        });

        if (conv.type === 'group' && !hasCurrentUser) {
          state.conversations.allIds = state.conversations.allIds.filter(id => id !== conv._id);
          delete state.conversations.byId[conv._id];
          if (state.activeConversationId === conv._id) {
            state.activeConversationId = null;
          }
        } else if (state.conversations.byId[conv._id]) {
          upsertConversation(state, conv);
        }
      }
    },

    /** New conversation created for current user (added to group etc.) */
    onConversationNew(state, action) {
      upsertConversation(state, action.payload);
    },

    /** Typing start for a user in a conversation */
    onTypingStart(state, action) {
      const { conversationId, userId } = action.payload;
      if (!state.typingUsers[conversationId]) {
        state.typingUsers[conversationId] = [];
      }
      if (!state.typingUsers[conversationId].includes(userId)) {
        state.typingUsers[conversationId].push(userId);
      }
    },

    /** Typing stop for a user in a conversation */
    onTypingStop(state, action) {
      const { conversationId, userId } = action.payload;
      if (state.typingUsers[conversationId]) {
        state.typingUsers[conversationId] = state.typingUsers[conversationId].filter(
          (id) => id !== userId
        );
      }
    },

    // ─── Calling Reducers ───────────────────────────────────────────────────

    onCallRequest(state, action) {
      const { from, conversationId, type } = action.payload;
      state.call = {
        active: true,
        receiving: true,
        caller: from,
        callee: state.currentUserId,
        type,
        status: 'ringing',
        conversationId,
        isGroup: false,
        liveKitToken: null,
        liveKitUrl: null,
      };
    },

    onCallAccept(state, action) {
      const { token, url } = action.payload;
      if (state.call.active) {
        state.call.status = 'connected';
        if (token) state.call.liveKitToken = token;
        if (url)   state.call.liveKitUrl   = url;
      }
    },

    onCallReject(state) {
      state.call = {
        active: false, receiving: false, caller: null, callee: null,
        type: 'audio', status: 'idle', conversationId: null,
        isGroup: false, liveKitToken: null, liveKitUrl: null,
      };
    },

    onCallHangUp(state) {
      state.call = {
        active: false, receiving: false, caller: null, callee: null,
        type: 'audio', status: 'idle', conversationId: null,
        isGroup: false, liveKitToken: null, liveKitUrl: null,
        minimized: false,
      };
    },

    onGroupCallRequest(state, action) {
      const { from, conversationId, type } = action.payload;
      state.call = {
        active: true, receiving: true, caller: from,
        type, status: 'ringing', conversationId,
        isGroup: true, liveKitToken: null, liveKitUrl: null,
      };
    },

    onGroupCallEnd(state, action) {
      state.call = {
        active: false, receiving: false, caller: null, callee: null,
        type: 'audio', status: 'idle', conversationId: null,
        isGroup: false, liveKitToken: null, liveKitUrl: null,
        minimized: false,
      };
      // Mark this conversation's call as finished
      if (action.payload?.conversationId) {
        delete state.activeGroupCalls[action.payload.conversationId];
      }
    },

    onGroupCallActive(state, action) {
      const { conversationId, callerId, type } = action.payload;
      if (conversationId) {
        state.activeGroupCalls[conversationId] = { callerId, type, startedAt: Date.now() };
      }
    },

    onGroupCallInactive(state, action) {
      const { conversationId } = action.payload || {};
      if (conversationId) {
        delete state.activeGroupCalls[conversationId];
        delete state.callParticipants[conversationId];
      }
    },

    onCallParticipantsUpdate(state, action) {
      const { conversationId, participants } = action.payload || {};
      if (conversationId) {
        state.callParticipants[conversationId] = participants || [];
      }
    },

    setCallStatus(state, action) {
      state.call.status = action.payload;
    },

    setLocalCall(state, action) {
       const { 
         active, caller, callee, type, status, 
         conversationId, isGroup, liveKitToken, liveKitUrl,
         receiving 
       } = action.payload;

       state.call = {
         ...state.call,
         active,
         caller,
         callee,
         type,
         status,
         conversationId,
         isGroup: !!isGroup,
         receiving: !!receiving,
         liveKitToken: liveKitToken || null,
         liveKitUrl: liveKitUrl || null,
         minimized: false, // Ensure we start maximized
       };
    },

    toggleMinimizeCall(state) {
      state.call.minimized = !state.call.minimized;
    },

    /** User online/offline status update */
    onUpdateUserStatus(state, action) {
      const { userId, status } = action.payload; // userId is adminRef
      const statusValue = status || 'offline';

      // Update status in all conversations where this user is present
      Object.keys(state.conversations.byId).forEach((cid) => {
        const conv = state.conversations.byId[cid];
        if (conv.participants) {
          conv.participants.forEach((p) => {
            const pAdminRef = p.userId?.adminRef;
            const pOrgId = p.userId?._id || p.userId;

            let isMatch = false;

            // 1. Direct match by adminRef
            if (pAdminRef && pAdminRef.toString() === userId?.toString()) {
              isMatch = true;
            }
            // 2. Fallback: match by orgUserId using chatUsers map
            else if (pOrgId) {
              const matchedUser = state.chatUsers.find(
                (u) => 
                  (u._id === pOrgId.toString() || u.id === pOrgId.toString()) && 
                  u.adminRef?.toString() === userId?.toString()
              );
              if (matchedUser) isMatch = true;
            }

            if (isMatch) {
              p.status = statusValue;
              if (statusValue === 'offline') {
                p.lastActivity = new Date().toISOString();
              }
            }
          });
        }
      });

      // Also update in chatUsers list itself for consistency
      const userInList = state.chatUsers.find(
        (u) => u.adminRef?.toString() === userId?.toString()
      );
      if (userInList) {
        userInList.status = statusValue;
      }
    },

    /** Messages marked as read in a conversation */
    onReadReceipt(state, action) {
      const { conversationId, userId, readAt } = action.payload;
      if (state.messages[conversationId]) {
        state.messages[conversationId].list.forEach((msg) => {
          if (new Date(msg.createdAt) <= new Date(readAt)) {
            if (!msg.readBy) msg.readBy = [];
            const alreadyRead = msg.readBy.some((r) => r.userId === userId);
            if (!alreadyRead) {
              msg.readBy.push({ userId, readAt });
            }
          }
        });
      }

      // Sync unreadCount if the current user read messages (e.g. from another tab)
      if (state.currentUserId && (userId?.toString() === state.currentUserId.toString())) {
        const conv = state.conversations.byId[conversationId];
        if (conv) {
          const participant = conv.participants?.find(part => part.isCurrentUser);
          if (participant && participant.unreadCount > 0) {
            state.totalUnread = Math.max(0, state.totalUnread - participant.unreadCount);
            participant.unreadCount = 0;
          }
        }
      }
    },

    updateSettings(state, action) {
      state.settings = { ...state.settings, ...action.payload };
    },

    clearChatError(state) {
      state.error = null;
    },

    clearChat(state) {
      state.conversations = { byId: {}, allIds: [] };
      state.messages = {};
      state.typingUsers = {};
      state.totalUnread = 0;
      state.activeConversationId = null;
    },
  },
  extraReducers: (builder) => {
    // fetchConversations
    builder
      .addCase(fetchConversations.pending, (state) => {
        state.loading.conversations = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading.conversations = false;
      const { data, activeUnread, directUnread, groupUnread, archivedUnread, archivedCount, userId, orgUserId, settings } = action.payload;

      if (settings) {
        state.settings = {
          showEmail: settings.showEmail !== false,
          showPhone: settings.showPhone !== false,
          showPhoto: settings.showPhoto !== false,
        };
      }

      if (userId) state.currentUserId = userId;
      if (orgUserId) state.currentOrgUserId = orgUserId;
      
      if (activeUnread !== undefined) state.activeUnread = activeUnread;
      if (directUnread !== undefined) state.directUnread = directUnread;
      if (groupUnread !== undefined) state.groupUnread = groupUnread;
      if (archivedUnread !== undefined) state.archivedUnread = archivedUnread;
      if (archivedCount !== undefined) state.archivedCount = archivedCount;
      state.totalUnread = state.activeUnread + state.archivedUnread;

      const newById = {};
      const newAllIds = [];

      data.forEach((conv) => {
            // Mark current user if known (Internal logic similar to upsertConversation)
            if (conv.participants && (state.currentUserId || state.currentOrgUserId)) {
              conv.participants.forEach(p => {
                const pUserId = p.userId?._id || p.userId;
                const pAdminRef = p.userId?.adminRef;
                const isMeByAdmin = state.currentUserId && pAdminRef?.toString() === state.currentUserId.toString();
                const isMeByOrg = state.currentOrgUserId && pUserId?.toString() === state.currentOrgUserId.toString();
                if (isMeByAdmin || isMeByOrg) {
                    p.isCurrentUser = true;
                }
              });
            }
            newById[conv._id] = conv;
            newAllIds.push(conv._id);
        });

      state.conversations.byId = newById;
      state.conversations.allIds = newAllIds;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loading.conversations = false;
        state.error = action.payload;
      });

    // fetchMessages
    builder
      .addCase(fetchMessages.pending, (state) => {
        state.loading.messages = true;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.loading.messages = false;
        const { conversationId, messages, hasMore } = action.payload;
        if (!state.messages[conversationId]) {
          state.messages[conversationId] = { list: [], hasMore: true, cursor: null };
        }
        const existing = state.messages[conversationId];
        // Prepend older messages
        const existingIds = new Set(existing.list.map(m => m._id));
        const newMessages = messages.filter(m => !existingIds.has(m._id));
        existing.list = [...newMessages, ...existing.list];
        existing.hasMore = hasMore;
        if (messages.length > 0) {
          existing.cursor = messages[0].createdAt;
        }
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.loading.messages = false;
        state.error = action.payload;
      });

    // createConversation
    builder.addCase(createConversation.fulfilled, (state, action) => {
      upsertConversation(state, action.payload);
      state.activeConversationId = action.payload._id;
    });

    // sendMessage
    builder
      .addCase(sendMessage.pending, (state) => { state.loading.sending = true; })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.loading.sending = false;
        upsertMessage(state, action.payload);
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.loading.sending = false;
        state.error = action.payload;
      });

    // sendMediaMessage
    builder
      .addCase(sendMediaMessage.pending, (state, action) => {
        state.loading.sending = true;
        const { conversationId, file } = action.meta.arg || {};
        if (conversationId && file) {
          let type = 'file';
          if (file.type?.startsWith('image/')) type = 'image';
          else if (file.type?.startsWith('video/')) type = 'video';
          else if (file.type?.startsWith('audio/')) type = 'audio';

          const tempId = `temp-${Date.now()}`;
          const tempMessage = {
            _id: tempId,
            id: tempId,
            conversationId,
            body: file.name,
            type,
            mediaUrl: URL.createObjectURL(file), // local blob URL for instant preview
            mediaOriginalName: file.name,
            mediaMimeType: file.type,
            mediaSize: file.size,
            status: 'uploading',
            progress: 0,
            createdAt: new Date().toISOString(),
            sender: state.currentUserId || state.currentOrgUserId,
            readBy: [],
            deliveredTo: [],
          };

          if (!state.messages[conversationId]) {
            state.messages[conversationId] = { list: [], hasMore: false };
          }
          state.messages[conversationId].list.push(tempMessage);
        }
      })
      .addCase(sendMediaMessage.fulfilled, (state, action) => {
        state.loading.sending = false;
        const { conversationId } = action.meta.arg || {};
        if (conversationId && state.messages[conversationId]) {
          state.messages[conversationId].list = state.messages[conversationId].list.filter(
            (m) => !String(m._id).startsWith('temp-')
          );
        }
        upsertMessage(state, action.payload);
      })
      .addCase(sendMediaMessage.rejected, (state, action) => {
        state.loading.sending = false;
        state.error = action.payload;
        const { conversationId } = action.meta.arg || {};
        if (conversationId && state.messages[conversationId]) {
          state.messages[conversationId].list = state.messages[conversationId].list.filter(
            (m) => !String(m._id).startsWith('temp-')
          );
        }
      });

    // editMessage
    builder.addCase(editMessage.fulfilled, (state, action) => {
      upsertMessage(state, action.payload);
    });

    // deleteMessage
    builder.addCase(deleteMessage.fulfilled, (state, action) => {
      const { messageId, conversationId } = action.payload;
      if (state.messages[conversationId]) {
        const idx = state.messages[conversationId].list.findIndex(m => m._id === messageId);
        if (idx >= 0) {
          state.messages[conversationId].list[idx].deletedAt = new Date().toISOString();
        }
      }
    });

    // reactToMessage
    builder.addCase(reactToMessage.fulfilled, (state, action) => {
      upsertMessage(state, action.payload);
    });

    // markAsRead
    builder.addCase(markAsRead.fulfilled, (state, action) => {
      const { conversationId } = action.payload;
      const conv = state.conversations.byId[conversationId];
      if (conv) {
        const participant = conv.participants?.find(part => part.isCurrentUser);
        if (participant && participant.unreadCount > 0) {
          const count = participant.unreadCount;
          if (participant.isArchived) {
            state.archivedUnread = Math.max(0, state.archivedUnread - count);
          } else {
            state.activeUnread = Math.max(0, state.activeUnread - count);
            if (conv.type === 'group') state.groupUnread = Math.max(0, state.groupUnread - count);
            else state.directUnread = Math.max(0, state.directUnread - count);
          }
          
          state.totalUnread = Math.max(0, state.totalUnread - count);
          participant.unreadCount = 0;
        }
      }
    });

    // muteConversation
    builder.addCase(muteConversation.fulfilled, (state, action) => {
      const { conversationId, mutedUntil } = action.payload;
      const conv = state.conversations.byId[conversationId];
      if (conv) {
        const participant = conv.participants?.find((p) => p.isCurrentUser);
        if (participant) {
          participant.mutedUntil = mutedUntil;
        }
      }
    });

    // archiveConversation
    builder.addCase(archiveConversation.fulfilled, (state, action) => {
      const { conversationId, archive } = action.payload;
      const conv = state.conversations.byId[conversationId];
      if (conv) {
        const participant = conv.participants?.find((p) => p.isCurrentUser);
        if (participant) {
            const count = participant.unreadCount || 0;
            // If archiving: move unread from active to archived
            // If unarchiving: move from archived to active
            if (archive && !participant.isArchived) {
                state.activeUnread = Math.max(0, state.activeUnread - count);
                if (conv.type === 'group') state.groupUnread = Math.max(0, state.groupUnread - count);
                else state.directUnread = Math.max(0, state.directUnread - count);
                state.archivedUnread += count;
                state.archivedCount = (state.archivedCount || 0) + 1;
            } else if (!archive && participant.isArchived) {
                state.archivedUnread = Math.max(0, state.archivedUnread - count);
                state.activeUnread += count;
                if (conv.type === 'group') state.groupUnread += count;
                else state.directUnread += count;
                state.archivedCount = Math.max(0, (state.archivedCount || 0) - 1);
            }
            participant.isArchived = archive;
        }
      }
      // Update local allIds for immediate UI feedback
      if (archive) {
        // Archiving: remove from current list
        state.conversations.allIds = state.conversations.allIds.filter(id => id !== conversationId);
      } else if (!state.conversations.allIds.includes(conversationId)) {
        // Unarchiving: add to the top of the list if not already there
        state.conversations.allIds.unshift(conversationId);
      }
    });

    // fetchChatUsers
    builder
      .addCase(fetchChatUsers.pending, (state) => {
        state.loading.chatUsers = true;
      })
      .addCase(fetchChatUsers.fulfilled, (state, action) => {
        state.loading.chatUsers = false;
        state.chatUsers = action.payload;
      })
      .addCase(fetchChatUsers.rejected, (state, action) => {
        state.loading.chatUsers = false;
        state.error = action.payload;
      });

    // Participant and message pinning thunk reducers
    [addParticipant, removeParticipant, updateParticipantRole, pinMessage, unpinMessage].forEach(thunk => {
      builder.addCase(thunk.fulfilled, (state, action) => {
        if (action.payload?._id) {
          const conv = action.payload;
          // If the current user is no longer a participant in the group conversation, remove it from list
          const hasCurrentUser = conv.participants?.some(p => {
            const pUserId = p.userId?._id || p.userId;
            const pAdminRef = p.userId?.adminRef;
            const isMeByAdmin = state.currentUserId && pAdminRef?.toString() === state.currentUserId.toString();
            const isMeByOrg = state.currentOrgUserId && pUserId?.toString() === state.currentOrgUserId.toString();
            return isMeByAdmin || isMeByOrg;
          });

          if (conv.type === 'group' && !hasCurrentUser) {
            state.conversations.allIds = state.conversations.allIds.filter(id => id !== conv._id);
            delete state.conversations.byId[conv._id];
            if (state.activeConversationId === conv._id) {
              state.activeConversationId = null;
            }
          } else {
            upsertConversation(state, conv);
          }
        }
      });
    });

    // votePoll
    builder.addCase(votePoll.fulfilled, (state, action) => {
      upsertMessage(state, action.payload);
    });

    // updatePrivacySettings
    builder.addCase(updatePrivacySettings.fulfilled, (state, action) => {
      if (action.payload) {
        state.settings = {
          showEmail: action.payload.showEmail !== false,
          showPhone: action.payload.showPhone !== false,
          showPhoto: action.payload.showPhoto !== false,
        };
      }
    });
  }
});

export const {
  updateUploadProgress,
  setActiveConversation,
  resetUnread,
  onNewMessage,
  onMessageUpdated,
  onConversationUpdated,
  onConversationNew,
  onTypingStart,
  onTypingStop,
  onUpdateUserStatus,
  onReadReceipt,
  updateSettings,
  clearChatError,
  clearChat,
  onCallRequest,
  onCallAccept,
  onCallReject,
  onCallHangUp,
  onGroupCallRequest,
  onGroupCallEnd,
  onGroupCallActive,
  onGroupCallInactive,
  onCallParticipantsUpdate,
  setCallStatus,
  setLocalCall,
  toggleMinimizeCall,
} = chatSlice.actions;

// ----------------------------------------------------------------------

export const saveCallLog = (conversationId, callData) => async () => {
  try {
    await axiosInstance.post(`/api/admin/chat/conversations/${conversationId}/call-log`, callData);
  } catch (error) {
    console.error('saveCallLog error:', error);
  }
};

export default chatSlice.reducer;
